import React, { FC } from 'react';

import { IconButton, LinkButton } from '@adsk/alloy-react-button';
import FileHandler from '@adsk/alloy-react-file-handler';
import { DownloadIcon } from '@adsk/alloy-react-icon';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import theme from '@adsk/alloy-react-theme';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import AttachmentListItemErrorState from './AttachmentFileHandlerErrorState';
import AttachmentFileHandlerIconPlaceholder from './AttachmentFileHandlerIconPlaceholder';
import AttachmentFileHandlerThumbnail from './AttachmentFileHandlerThumbnail';
import getFileHandlerVariant from './getFileHandlerVariant';
import { ATTACHMENTS_LIST_VARIANTS } from '../../../consts';
import { useTranslation } from '../../../i18n';
import { AttachmentItemViewProps } from '../../../types';
import AttachmentContextualMenu from '../AttachmentItemContextualMenu';
import AttachmentItemIcon from '../AttachmentItemIcon';

const AttachmentFileHandlerItem: FC<AttachmentItemViewProps> = ({
  thumbnail,
  onDelete,
  metadata,
  isContextualMenuOpen,
  setIsContextualMenuOpen,
  name,
  error,
  allowDelete,
  onClick,
  onDownload,
  onRetry,
  allowDownload,
  allowRetry,
  isUploading,
  isDeleting,
  customItemActions,
  isDeleted,
  condensed,
  showUploadSuccessIndication,
}) => {
  const { t } = useTranslation();

  if (error) {
    return (
      <AttachmentListItemErrorState
        error={error}
        name={name}
        onDelete={onDelete}
        allowRetry={allowRetry}
        onRetry={onRetry}
      />
    );
  }

  const isLoading = isDeleting || isUploading;
  const showThumbnail = !!thumbnail && !condensed;
  const shouldRenderFileName = !isLoading && !isDeleted;
  const showMetaData = !(error || isLoading || condensed);
  const shouldRenderSecondaryAction =
    !isLoading && allowDelete && !condensed && !showUploadSuccessIndication;

  return (
    <>
      <FileHandler
        style={
          condensed
            ? {
                boxShadow: 'none',
                alignItems: 'center',
                height: 32,
                padding: 0,
                marginBottom: '4px',
                flexBasis: 'auto',
              }
            : { alignItems: 'center', minWidth: 250 }
        }
        alertMessage={error}
        renderMainAction={
          isLoading
            ? undefined
            : allowDownload
              ? () => (
                  <Tooltip
                    content={t('DOWNLOAD')}
                    getTargetProps={() => ({
                      style: { display: 'flex' },
                    })}
                  >
                    <IconButton
                      onClick={onDownload}
                      renderIcon={DownloadIcon.renderIcon}
                      style={{ color: theme.colors.charcoal900 }}
                    />
                  </Tooltip>
                )
              : undefined
        }
        renderIcon={() => {
          if (showThumbnail) {
            return (
              <AttachmentFileHandlerThumbnail
                thumbnail={thumbnail}
                onClick={onClick}
              />
            );
          }

          return condensed ? (
            <AttachmentItemIcon
              name={name}
              style={{
                marginRight: '8px',
                color:
                  isDeleted || isLoading
                    ? theme.colors.charcoal400
                    : theme.colors.charcoal700,
              }}
            />
          ) : (
            <AttachmentFileHandlerIconPlaceholder>
              <AttachmentItemIcon
                name={name}
                style={{
                  color:
                    isDeleted || isLoading
                      ? theme.colors.charcoal400
                      : theme.colors.charcoal700,
                }}
              />
            </AttachmentFileHandlerIconPlaceholder>
          );
        }}
        name={name}
        renderFileName={
          shouldRenderFileName
            ? () => (
                <LinkButton
                  onClick={onClick}
                  style={{
                    width: '100%',
                    textAlign: 'start',
                    height: condensed ? 24 : undefined,
                  }}
                >
                  <OverflowTooltip
                    data-testid="AttachmentFileHandlerItemTooltip"
                    content={name}
                  >
                    {name}
                  </OverflowTooltip>
                </LinkButton>
              )
            : undefined
        }
        metadata={showMetaData ? metadata : undefined}
        variant={getFileHandlerVariant({
          error,
          loading: isLoading,
          isDeleted,
          success: showUploadSuccessIndication,
        })}
        renderSecondaryAction={
          shouldRenderSecondaryAction
            ? () => (
                <AttachmentContextualMenu
                  show={isContextualMenuOpen}
                  onHide={() => setIsContextualMenuOpen(false)}
                  onShow={() => setIsContextualMenuOpen(true)}
                  allowDelete={allowDelete}
                  onDelete={onDelete}
                  allowDownload={false}
                  variant={ATTACHMENTS_LIST_VARIANTS.LIST}
                  customItemActions={customItemActions}
                />
              )
            : undefined
        }
      />
    </>
  );
};

export default WithAlloySuspense(AttachmentFileHandlerItem);
